import { useMemo } from 'react';
import { useBuilds, useProjects } from '@/api/hooks';
import { stagger } from '@/lib/motion';
import { Icon } from '@/components/Icon';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { kpiStats, kpiTiles, type KpiTile } from './kpiModel';

function KpiValue({ tile, loading }: { tile: KpiTile; loading: boolean }) {
  if (loading) return <Skeleton className="mt-3 h-7 w-16" />;
  return (
    <div className="tabular mt-3 font-mono text-[28px] font-semibold leading-none text-foreground" aria-label={tile.value === null ? 'No data yet' : undefined}>
      {tile.value ?? '—'}
    </div>
  );
}

function Tile({ tile, index, loading }: { tile: KpiTile; index: number; loading: boolean }) {
  return (
    <Card data-testid={tile.testId} className="enter p-4 sm:p-5" style={stagger(index)}>
      <div className="flex items-center justify-between gap-2 text-sm text-muted-foreground">
        <span className="truncate max-sm:hidden">{tile.label}</span>
        <span className="truncate sm:hidden">{tile.short}</span>
        <Icon name={tile.icon} size={18} className="text-subtle" />
      </div>
      <KpiValue tile={tile} loading={loading && index < 2} />
      <div aria-hidden className="mt-4 h-1.5 overflow-hidden rounded-full bg-secondary">
        <div className="h-full rounded-full bg-succeeded transition-[width] duration-300" style={{ width: `${tile.bar}%` }} />
      </div>
      <div className="mt-3 truncate font-mono text-xs text-subtle">{tile.reason}</div>
    </Card>
  );
}

/**
 * The tiles of screen 01: "Builds today" and "Success rate (7d)", from `GET /builds?createdAfter=<7 days>` of the
 * current project (cursor-paged, up to 200 rows). Median build time and queue wait return when the API reports them.
 */
export function KpiTiles({ projectId }: { projectId?: string }) {
  const projects = useProjects();
  const pid = projectId ?? projects.data?.[0]?.id;
  const since = useMemo(() => new Date(Date.now() - 7 * 24 * 3600_000).toISOString(), []);
  const builds = useBuilds({ projectId: pid, createdAfter: since, limit: 200 }, Boolean(pid));
  const rows = builds.data?.pages.flatMap((p) => p.data);
  const loading = Boolean(pid) && builds.isLoading && !rows;
  const stats = useMemo(() => kpiStats(rows, Boolean(builds.hasNextPage), new Date()), [rows, builds.hasNextPage]);
  const tiles = kpiTiles(stats, { isError: builds.isError, hasProject: Boolean(pid) });

  return (
    <div className="grid grid-cols-2 gap-4">
      {tiles.map((t, i) => (
        <Tile key={t.label} tile={t} index={i} loading={loading} />
      ))}
    </div>
  );
}
